import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface AuthState {
  wallet: string | null;
  walletChecksum: string | null;
  loading: boolean;
}

interface AuthCtx extends AuthState {
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  connect: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

const NETWORKS: Record<number, { chainName: string; rpcUrls: string[]; blockExplorerUrls: string[] }> = {
  42161: { chainName: "Arbitrum One", rpcUrls: ["https://arb1.arbitrum.io/rpc"], blockExplorerUrls: ["https://arbiscan.io"] },
  421614: { chainName: "Arbitrum Sepolia", rpcUrls: ["https://sepolia-rollup.arbitrum.io/rpc"], blockExplorerUrls: ["https://sepolia.arbiscan.io"] },
};

declare global {
  interface Window {
    ethereum?: {
      request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
    };
  }
}

async function ensureChain() {
  let chainId = 42161;
  try {
    const cfg = await fetch("/api/marketplace/config").then((r) => r.json());
    if (cfg?.chain_id) chainId = Number(cfg.chain_id);
  } catch {
    /* default Arbitrum One */
  }
  if (!window.ethereum) throw new Error("MetaMask required");
  const targetHex = "0x" + chainId.toString(16);
  const current = (await window.ethereum.request({ method: "eth_chainId" })) as string;
  if (String(current).toLowerCase() === targetHex) return;
  try {
    await window.ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: targetHex }],
    });
  } catch (err) {
    const e = err as { code?: number };
    if ((e.code === 4902 || e.code === -32603) && NETWORKS[chainId]) {
      await window.ethereum.request({
        method: "wallet_addEthereumChain",
        params: [{
          chainId: targetHex,
          ...NETWORKS[chainId],
          nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
        }],
      });
    } else {
      throw err;
    }
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ wallet: null, walletChecksum: null, loading: true });

  const refresh = useCallback(async () => {
    try {
      const r = await fetch("/api/auth/me", { credentials: "same-origin" });
      const data = await r.json();
      setState({
        wallet: data.wallet || null,
        walletChecksum: data.wallet_checksum || data.wallet || null,
        loading: false,
      });
    } catch {
      setState({ wallet: null, walletChecksum: null, loading: false });
    }
  }, []);

  const logout = useCallback(async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
    await refresh();
  }, [refresh]);

  const connect = useCallback(async () => {
    if (!window.ethereum) {
      alert("MetaMask required");
      return;
    }
    const accounts = (await window.ethereum.request({ method: "eth_requestAccounts" })) as string[];
    const address = accounts?.[0];
    if (!address) throw new Error("Aucune adresse renvoyée par MetaMask");

    await ensureChain();

    const nonceRes = await fetch("/api/auth/nonce", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ address }),
    });
    if (!nonceRes.ok) throw new Error(`Nonce HTTP ${nonceRes.status}`);
    const { message } = await nonceRes.json();

    const signature = (await window.ethereum.request({
      method: "personal_sign",
      params: [message, address],
    })) as string;

    const verifyRes = await fetch("/api/auth/verify", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message, signature }),
    });
    if (!verifyRes.ok) {
      const err = await verifyRes.json().catch(() => ({}));
      throw new Error(err.error || `Verify HTTP ${verifyRes.status}`);
    }
    await refresh();
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo<AuthCtx>(() => ({ ...state, refresh, logout, connect }), [state, refresh, logout, connect]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth must be used within AuthProvider");
  return c;
}

export function shortAddr(addr: string | null | undefined) {
  if (!addr) return "";
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}
